import { useMemo } from 'react';
import { Box, Card, CardActionArea, Chip, CssBaseline, Stack, ThemeProvider, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { createAppTheme } from '../../theme/theme';
import { buildApplicationData } from '../../application/platform/application-app-service';
import { getProductName, PLATFORM_NAME } from '../../application/platform/govhub-brand';
import LocalEnvironment, { localModules } from '../../application/platform/local-environment';
import { buildModuleHostname } from '../../application/platform/module-hostname';

// Tela do hostname "apps-sand.<empresa>" (ou "apps."): lista os módulos e abre cada um no seu próprio hostname,
// com a sessão e as APIs de sempre. A aplicação não é carregada aqui.
export function ModuleLauncher() {
    const hostname = window.location.hostname.toLowerCase();
    const isSandbox = hostname.includes('sand');
    const multitenant = buildApplicationData(hostname).multitenant;
    const tenant = multitenant ? LocalEnvironment.getTenant(multitenant) : undefined;

    const theme = useMemo(() => createAppTheme(), []);

    const moduleUrl = (moduleName: string) =>
        `${window.location.protocol}//${buildModuleHostname(hostname, moduleName.toLowerCase())}/`;

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', p: 2 }}>
                <Box sx={{ width: '100%', maxWidth: 720, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1, p: { xs: 2, sm: 4 } }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="overline" color="text.secondary">{PLATFORM_NAME}{tenant ? ` · ${tenant.name}` : ''}</Typography>
                        {isSandbox && <Chip label="Sandbox" size="small" color="warning" variant="outlined" />}
                    </Stack>
                    <Typography variant="h5" fontWeight={600} gutterBottom>Escolha o módulo</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Cada módulo abre no seu próprio endereço, com login separado.
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(200px, 1fr))' }, gap: 1.5 }}>
                        {localModules.map(module => {
                            const color = createAppTheme(module.moduleId).palette.primary.main;
                            return (
                                <Card key={module.moduleId} variant="outlined" sx={{ '&:hover': { borderColor: color } }}>
                                    <CardActionArea component="a" href={moduleUrl(module.moduleName)} sx={{ p: 1.5, height: '100%' }}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                                <Typography fontWeight={600}>{getProductName(module.moduleId)}</Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">{module.moduleDescription}</Typography>
                                            </Box>
                                            <OpenInNewIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                                        </Stack>
                                    </CardActionArea>
                                </Card>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    );
}
